import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, message, Spin, Statistic, Row, Col, Card, Select, Input } from 'antd';
import { CheckCircleOutlined, MinusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { resellerService } from '../../services/reseller.service';
import { CouponSetDetail, CouponCode } from '../../types/reseller.types';
import Pagination from './Pagination';

const { Option } = Select;

interface CouponSetDetailModalProps {
  couponSetId: number | null;
  visible: boolean;
  onClose: () => void;
}

const CouponSetDetailModal: React.FC<CouponSetDetailModalProps> = ({
  couponSetId,
  visible,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CouponSetDetail | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 50;

  useEffect(() => {
    if (visible && couponSetId) {
      loadData();
    }
  }, [visible, couponSetId, page, statusFilter, searchQuery]);

  const loadData = async () => {
    if (!couponSetId) return;

    setLoading(true);
    try {
      const response = await resellerService.getCouponSetDetail(
        couponSetId, 
        page, 
        pageSize,
        statusFilter !== 'all' ? statusFilter : undefined,
        searchQuery || undefined
      );
      if (response.isSuccess && response.responseObject) {
        setData(response.responseObject);
      } else {
        message.error('Failed to load coupon set details');
      }
    } catch (error) {
      message.error('Failed to load coupon set details');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPage(1);
    setData(null);
    setStatusFilter('all');
    setSearchQuery('');
    onClose();
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(1); 
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1); 
  };

  const columns: ColumnsType<CouponCode> = [
    {
      title: 'Full Code',
      dataIndex: 'fullCode',
      key: 'fullCode',
      width: 180,
      render: (fullCode: string) => <span className="code-mono">{fullCode}</span>
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        if (record.isRedeemed) {
          return (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Redeemed
            </Tag>
          );
        }
        if (record.isActive) {
          return (
            <Tag icon={<CheckCircleOutlined />} color="blue">
              Available
            </Tag>
          );
        }
        return (
          <Tag icon={<MinusCircleOutlined />} color="default">
            Inactive
          </Tag>
        );
      }
    },
    {
      title: 'Redeemed On',
      dataIndex: 'redeemedOn',
      key: 'redeemedOn',
      width: 180,
      render: (date: string | undefined) =>
        date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: 'Redeemed By',
      dataIndex: 'redeemedByEmail',
      key: 'redeemedByEmail',
      ellipsis: true,
      render: (email: string | undefined) => email || '-'
    }
  ];

  return (
    <Modal
      title={
        data ? (
          <div>
            <h3>{data.name}</h3>
            <p className="modal-subtitle">
              {data.resellerName} ({data.resellerCode})
            </p>
          </div>
        ) : (
          'Coupon Set Details'
        )
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={1200}
      className="coupon-set-detail-modal"
      style={{ top: 20 }}
      zIndex={99999}
    >
      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : data ? (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Codes"
                  value={data.totalCodes}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Redeemed"
                  value={data.redeemedCodes}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Available"
                  value={data.availableCodes}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={data.totalRevenue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          <div className="coupon-set-price">
            <strong>Price per license:</strong> ${data.pricePerLicense.toFixed(2)}
          </div>

          <div className="modal-filters">
            <Input
              placeholder="Search by code..."
              prefix={<SearchOutlined />}
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: 150 }}
            >
              <Option value="all">All Status</Option>
              <Option value="available">Available</Option>
              <Option value="redeemed">Redeemed</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </div>

          <Table
            columns={columns}
            dataSource={data.codes}
            rowKey="id"
            pagination={false}
            loading={loading}
            size="small"
            style={{ marginTop: 16 }}
          />

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              currentPage={data.currentPage}
              totalPages={data.totalPages}
              totalCount={data.totalCodes}
              onPageChange={setPage}
              disabled={loading}
            />
          </div>
        </>
      ) : null}
    </Modal>
  );
};

export default CouponSetDetailModal;
